// ── SPORTCENTER · PRODUKTER ───────────────────────────────────────
// Produktstatistiken personalen faktiskt frågar efter: hur många skidor gick
// det, vad sålde bäst, och hur ser det ut över en period man väljer själv.
//
// Backend levererar hela historiken (dag × kategori) i ETT anrop utanför
// snapshoten. Perioden byts därför i klienten — inga nya nätanrop när man
// klickar runt, och godtyckliga datumspann kostar inget extra.
import React, { useEffect, useMemo, useState } from 'react';
import { getSportcenterProdukter } from '../qlik';
import { raStartYear } from '../utils';
import { VisaFlerRad, Metric } from '../components/SharedComponents';

const SPORT_COLOR = '#1A3D2B';
const kr = (v) => Math.round(v).toLocaleString('sv-SE') + ' kr';
const p2 = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;

export const knappStil = (aktiv) => ({
  background: aktiv ? SPORT_COLOR : 'transparent',
  color: aktiv ? '#fff' : 'var(--text-secondary)',
  border: '1px solid var(--border)', borderRadius: 20,
  padding: '3px 10px', fontSize: 11, cursor: 'pointer',
  fontFamily: 'system-ui,sans-serif', transition: 'all 0.15s',
});

const datumFalt = {
  border: '1px solid var(--border)', borderRadius: 8, padding: '3px 8px', fontSize: 11.5,
  fontFamily: 'system-ui,sans-serif', background: 'transparent', color: 'var(--text)',
};

// Räkenskapsåret är säsongen — jul–jun. "Allt" finns för att kategorikartan
// ska gå att rita i lågsäsong, då ingen vinterkategori sålts på månader.
export function periodSpann(key) {
  const idag = new Date();
  const ra = raStartYear();
  switch (key) {
    case 'idag':      return { from: iso(idag), to: iso(idag) };
    case 'igar': {
      const d = new Date(idag); d.setDate(d.getDate() - 1);
      return { from: iso(d), to: iso(d) };
    }
    case 'vecka': {
      const d = new Date(idag); d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      return { from: iso(d), to: iso(idag) };
    }
    case 'manad':     return { from: `${idag.getFullYear()}-${p2(idag.getMonth() + 1)}-01`, to: iso(idag) };
    case 'sasong':    return { from: `${ra}-07-01`,     to: `${ra + 1}-06-30` };
    case 'fjol':      return { from: `${ra - 1}-07-01`, to: `${ra}-06-30` };
    case 'allt':      return { from: '2000-01-01',      to: '2099-12-31' };
    default:          return { from: `${ra}-07-01`,     to: `${ra + 1}-06-30` };
  }
}

export const PERIOD_KNAPPAR = [
  { key: 'idag',   label: 'Idag'   },
  { key: 'igar',   label: 'Igår'   },
  { key: 'vecka',  label: 'Vecka'  },
  { key: 'manad',  label: 'Månad'  },
  { key: 'sasong', label: `Säsong ${String(raStartYear() % 100).padStart(2, '0')}/${String((raStartYear() + 1) % 100).padStart(2, '0')}` },
  { key: 'fjol',   label: `Säsong ${String((raStartYear() - 1) % 100).padStart(2, '0')}/${String(raStartYear() % 100).padStart(2, '0')}` },
  { key: 'allt',   label: 'Allt'   },
];

// Hämtar en gång per sidladdning och delas av alla kort som behöver den.
export function useSportcenterProdukter() {
  const [data, setData] = useState(null);
  useEffect(() => { getSportcenterProdukter().then(setData).catch(() => setData({ kategorier: [], rows: [] })); }, []);
  return data;
}

// Rårader → summerat per kategori i valt spann.
export function aggregera(data, from, to) {
  if (!data) return [];
  const per = new Map();
  for (const [datum, ki, antal, intakt] of data.rows || []) {
    if (datum < from || datum > to) continue;
    const namn = data.kategorier[ki];
    const cur = per.get(namn) || { name: namn, antal: 0, intakt: 0, dagar: new Set() };
    cur.antal  += antal  || 0;
    cur.intakt += intakt || 0;
    cur.dagar.add(datum);
    per.set(namn, cur);
  }
  return [...per.values()].map((p) => ({ ...p, dagar: p.dagar.size }));
}

// Delad periodväljare: knappar + fritt datumspann. Egna datum vinner.
export function PeriodValjare({ vald, setVald, from, setFrom, to, setTo }) {
  const eget = !!(from && to && from <= to);
  return (
    <>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
        {PERIOD_KNAPPAR.map(({ key, label }) => (
          <button key={key} onClick={() => { setVald(key); setFrom(''); setTo(''); }} style={knappStil(!eget && vald === key)}>{label}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10, fontSize: 11.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>
        <span>Från</span>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="Från datum" style={datumFalt} />
        <span>till</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="Till datum" style={datumFalt} />
        {(from || to) && <button onClick={() => { setFrom(''); setTo(''); }} style={knappStil(false)}>Rensa</button>}
      </div>
    </>
  );
}

export default function SportcenterProdukter() {
  const data = useSportcenterProdukter();
  const [vald, setVald]   = useState('sasong');
  const [from, setFrom]   = useState('');
  const [to, setTo]       = useState('');
  const [sok, setSok]     = useState('');
  const [sortBy, setSortBy] = useState('intakt'); // 'intakt' | 'antal'
  const [visaAlla, setVisaAlla] = useState(false);

  const eget  = !!(from && to && from <= to);
  const spann = eget ? { from, to } : periodSpann(vald);

  const rader = useMemo(() => aggregera(data, spann.from, spann.to), [data, spann.from, spann.to]);

  const filtrerade = useMemo(() => {
    const q = sok.trim().toLowerCase();
    const bas = q ? rader.filter((r) => r.name.toLowerCase().includes(q)) : rader;
    return [...bas].sort((a, b) => b[sortBy] - a[sortBy]);
  }, [rader, sok, sortBy]);

  const totAntal  = filtrerade.reduce((s, r) => s + r.antal, 0);
  const totIntakt = filtrerade.reduce((s, r) => s + r.intakt, 0);
  const visade    = visaAlla ? filtrerade : filtrerade.slice(0, 20);
  const maxVal    = Math.max(...visade.map((r) => r[sortBy] || 0), 1);

  if (data === null) {
    return <div className="card"><p style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar produktdata…</p></div>;
  }
  if (data.fel || (data.rows || []).length === 0) {
    return (
      <div className="card">
        <div className="card-title">Produkter</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>
          Produktcachen är tom. Den fylls av den schemalagda hämtningen var 15:e minut — har backend precis rullats ut
          dröjer det ett kvart innan siffrorna finns här.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card" data-noexport>
        <div className="card-title">Period</div>
        <PeriodValjare vald={vald} setVald={setVald} from={from} setFrom={setFrom} to={to} setTo={setTo} />
        <div className="note">
          {eget ? `Eget spann ${spann.from} – ${spann.to}` : `${spann.from} – ${spann.to}`} ·
          {' '}Zettle-kategorier, belopp ex. moms. Intäkt utan Zettle-kategori (bl.a. Visbook-hyror) ligger utanför listan.
        </div>
      </div>

      <div className="grid-4">
        <Metric label="Sålda artiklar"    value={Math.round(totAntal).toLocaleString('sv-SE')} sub="st i perioden" />
        <Metric label="Försäljningsvärde" value={kr(totIntakt)} sub="ex. moms" />
        <Metric label="Unika kategorier"  value={String(filtrerade.length)} sub="olika produkter sålda" />
        <Metric label="Snittpris"         value={totAntal > 0 ? kr(totIntakt / totAntal) : '–'} sub="per såld artikel" />
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Sålda produkter — hela listan</div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              value={sok} onChange={(e) => setSok(e.target.value)} placeholder="Sök produkt (t.ex. skid)…"
              aria-label="Sök produkt"
              style={{ ...datumFalt, minWidth: 180 }}
            />
            <button onClick={() => setSortBy('intakt')} style={knappStil(sortBy === 'intakt')}>Belopp</button>
            <button onClick={() => setSortBy('antal')}  style={knappStil(sortBy === 'antal')}>Antal</button>
          </div>
        </div>

        {visade.map((r, i) => (
          <div key={r.name} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <div style={{ width: 18, fontSize: 10, color: '#bbb', textAlign: 'right', fontFamily: 'system-ui,sans-serif', flexShrink: 0 }}>{i + 1}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
                <span style={{ fontSize: 11.5, color: 'var(--text)', fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
                <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text)', fontFamily: 'system-ui,sans-serif', flexShrink: 0 }}>
                  {Math.round(r.antal)} st · {kr(r.intakt)}
                </span>
              </div>
              <div style={{ height: 3, background: 'var(--panel2)', borderRadius: 2 }}>
                <div style={{ height: '100%', width: `${Math.round((r[sortBy] || 0) / maxVal * 100)}%`, background: SPORT_COLOR, borderRadius: 2 }} />
              </div>
            </div>
          </div>
        ))}
        {filtrerade.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>
            {sok ? `Ingen produkt matchar "${sok}" i perioden.` : 'Ingen försäljning i perioden.'}
          </div>
        )}
        <VisaFlerRad total={filtrerade.length} expanded={visaAlla} onToggle={() => setVisaAlla((v) => !v)} initial={20} />

        {filtrerade.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border)', fontSize: 12, fontWeight: 700, fontFamily: 'system-ui,sans-serif' }}>
            <span>Summa — {filtrerade.length} kategorier</span>
            <span>{Math.round(totAntal).toLocaleString('sv-SE')} st · {kr(totIntakt)}</span>
          </div>
        )}
        <div className="note" style={{ marginTop: 10 }}>
          Källa: Qlik · Zettle [Produkt kategori Zettle]. Antal = Sum([Antal Zettle]), alltså sålda artiklar — inte antal köp.
        </div>
      </div>
    </>
  );
}
